import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Directive,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { Helper } from '../../../utils/helper';

import { ToastBarService } from '../../../services/toast-bar.service';
import { getUploadErrorMessage } from '../../../utils/upload-error-helper';

import { AccountInfo } from '@azure/msal-browser';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { blocksForSelect, IBlockForSelect } from '../../../utils/blocks-helper';

import { AccountInfoService } from '../../../services/account-info.service';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from '@openng/optimus-ui/autocomplete';
import { BaseModel } from '../../../models/email-template/base-model.interface';
import BlockModel from '../../../models/email-template/block-model.interface';
import { TemporaryMedia } from '../../../models/email-template/temporary-media.interface';
import { TranslatingTemplateStatus } from '../../../models/email-template/translatingTemplateStatus.interface';
import { TemplateSelection } from '../../../models/template/template-selection';
import { TypeEnum } from '../../../models/template/type.enum';
import { MetaStatusEnum } from '../../../models/whatsapp-template/meta-status-enum.enum';
import { IUser } from '../../../models/other/user.interface';
import { BlockManagementService } from '../../../services/block-management.service';
import { CampaignService } from '../../../services/campaign.service';
import { GlobalTemplateService } from '../../../services/global-template.service';
import { GreyOverlayService } from '../../../services/grey-overlay.service';
import { MediaManagementService } from '../../../services/media-management.service';
import { ResponsysService } from '../../../services/responsys.service';
import { SearchTemplateService } from '../../../services/search-template.service';
import { SignalRService } from '../../../services/signalr.service';
import { TemplateExportService } from '../../../services/template-export.service';
import { TemplateService } from '../../../services/template.service';
import { TranslateService } from '../../../services/translate.service';
import { ValidateInputService } from '../../../services/validate-input.service';
import { VariableMaxLengthService } from '../../../services/variable-max-length.service';
import { EditTemplateToPhaseConnections } from '../common/connect-to-campaign/edit-template-to-phase-connections.component';
import { NameCampaignDialogComponent, NameCampaignDialogResult } from '../common/name-campaign-dialog/name-campaign-dialog.component';
import { ICampaign, ICampaignPhase } from '../../../models/campaign/campaign.interface';
import {
  ALL_CHANNELS_FILTER,
  ChannelFilterChip,
  ChannelSearchMatch,
  ChannelSearchResult,
} from '../../../models/template/channel-search-result.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { ConnectTemplateToCampaignsRequest } from '../../../models/campaign/connect-template-to-campaigns-request.interface';
import { WhatsappService } from '../../../services/whatsapp.service';
import { BlockMaximumCount } from '../../../models/template/block-maximum-count.interface.';
import { BlockMinimumCount } from '../../../models/template/block-minimum-count.interface';
import { UniqueTextConstraint } from '../../../models/template/unique-text-constraint.interface';
import { RequiredFieldConstraint } from '../../../models/template/required-field-constraint.interface';

// RCA-487: the channels the cross-channel search popover shows as filter chips -
// the same set campaigns actually connect to (see IChannels), so ad-only
// channel types (Google/Meta) are left out even if they turn up in a search.
const CHANNEL_FILTER_ORDER: TypeEnum[] = [
  TypeEnum.EMAIL,
  TypeEnum.SMS,
  TypeEnum.WEBPUSH,
  TypeEnum.PUSH,
  TypeEnum.WHATSAPP,
  TypeEnum.VIBER,
  TypeEnum.RCS,
];

const CHANNEL_FILTER_LABELS: Partial<Record<TypeEnum, string>> = {
  [TypeEnum.EMAIL]: 'Email',
  [TypeEnum.SMS]: 'SMS',
  [TypeEnum.WEBPUSH]: 'Web Push',
  [TypeEnum.PUSH]: 'Push',
  [TypeEnum.WHATSAPP]: 'WhatsApp',
  [TypeEnum.VIBER]: 'Viber',
  [TypeEnum.RCS]: 'RCS',
};

// Clicking a cross-channel search result navigates to that channel's own
// editor route. Email is the only channel with a v2 editor today.
const CHANNEL_ROUTE_BASE_PATHS: Partial<Record<TypeEnum, string>> = {
  [TypeEnum.EMAIL]: '/email-template-v2/',
  [TypeEnum.SMS]: '/sms-template/',
  [TypeEnum.WEBPUSH]: '/web-push-template/',
  [TypeEnum.PUSH]: '/push-template/',
  [TypeEnum.WHATSAPP]: '/whatsapp-template/',
  [TypeEnum.VIBER]: '/viber-template/',
  [TypeEnum.RCS]: '/rcs-template/',
};

@Directive()
export abstract class BaseTemplateEditorComponent
  implements OnInit, OnDestroy, AfterViewChecked {
  // Channel identity — provided by the per-channel wrapper component.
  @Input() isTranslator = false;
  @Input({ required: true }) templateType!: TypeEnum;
  @Input({ required: true }) templateBasePath!: string;

  // Shared-template parameterization (defaults = email chrome; each channel passes what differs)
  @Input() channelLabel: string = 'Email';            // "Edit {label} template"
  @Input() namePlaceholder: string = 'Enter template name'; // p-autoComplete placeholder
  @Input() emptyMessage: string = 'No template found!';     // p-autoComplete emptyMessage
  @Input() heartbeatType: string = 'email';           // <app-heartbeat [type]>

  @Input() enableDragDrop: boolean = false;                         // email-only CDK drag/drop
  @Input() enableBlockToolbar: boolean = false;                     // email-only block dropdown + handle + add-block
  @Input() showResponsysButton: boolean = false;                    // email/sms/push/web-push
  @Input() showMetaSection: boolean = false;                        // whatsapp Meta badges + Send-to-Meta button
  @Input() showTranslateButton: boolean = true;                     // hidden only for whatsapp
  @Input() showSaveAsEmpty: boolean = false;                        // email only
  @Input() guardByProcessing: boolean = true;                       // rcs/viber set false (no Responsys/Meta-processing)
  @Input() showSidePreview: boolean = false;                        // google only: aggregate ad preview on the right
  @Input() blockMaximumCount: BlockMaximumCount[] = [];             // specified block type limits per template
  @Input() blockMinimumCount: BlockMinimumCount[] = [];             // specified block type treshold per template
  @Input() uniqueTextConstraints: UniqueTextConstraint[] = [];     // same-type blocks may not share normalized text per field group
  @Input() requiredFields: RequiredFieldConstraint[] = [];         // non-editor fields (media, dropdown) that must be filled to save

  // Behavioural parameterization (replaces former per-subclass method overrides)
  @Input() defaultBlocks: { name: string; count?: number; variables: { [key: string]: string } }[] = [];
  @Input() subscribeToMedia = false;                 // channels that consume staged media data
  @Input() supportsMediaDeletion = true;              // sms/push set false (no media deletion flow)
  @Input() sendsToMeta = false;                       // whatsapp: "save" sends to Meta instead of Responsys
  @Input() loadsMetaStatus = false;                   // whatsapp: hydrate metaStatus from the loaded template

  // Meta (WhatsApp) — lifted onto the base so the shared template type-checks for every channel.
  @Input() metaStatus: MetaStatusEnum | undefined;
  protected readonly MetaStatusEnum = MetaStatusEnum;
  protected readonly TypeEnum = TypeEnum;
  protected get metaCanEdit(): boolean {
    return (
      this.metaStatus === MetaStatusEnum.REJECTED ||
      this.metaStatus === MetaStatusEnum.FAILED ||
      this.metaStatus === MetaStatusEnum.NEVER_SENT
    );
  }

  protected confirmApprovalSend(whatsappModal: TemplateRef<any>): void {
    this.toogleDisableEdit(this.EDIT);
    this.activeModal = this.modalService.open(whatsappModal, {
      centered: true,
      backdrop: 'static',
    });
  }

  protected isTranslated: boolean | undefined;
  protected isBeingTranslated: boolean | undefined;
  protected isTemplateProcToResponsys: boolean | undefined;
  protected isApproved: boolean | undefined;

  protected EDIT = 'edit';
  protected READONLY = 'readonly';
  protected lockedBy: IUser = {
    name: '',
    username: '',
  };
  protected modifiedDate = null;
  protected isSubmitDisabled = false;
  protected clickSubject = new Subject<void>();
  // variable that shows who is using the template
  protected reorder: boolean = false;
  protected modalService = inject(NgbModal);
  protected activeModal!: NgbModalRef;
  protected outputModal!: NgbModalRef;
  protected currentUser: AccountInfo | undefined;
  protected viewMode: string = this.READONLY;
  protected templateName: string | null = null; // To hold the template name input
  protected originalTemplateName: string | undefined;
  protected isNew: boolean = true; // Holds the result: true/false or null if not submitted yet
  protected temporaryMedia: TemporaryMedia[] = [];
  protected mediaForDeletionWithinABlock: string[] = [];
  protected mediaForDeletion: string[] = [];

  // Template name suggestions
  protected suggestions: Array<TemplateSelection> | undefined;

  protected isResponsysSave: boolean = false;

  protected selectedBlocks: BlockModel[] = [];
  protected selectedBlock: BlockModel | undefined;
  protected selectedLanguage: string = 'en';
  protected idcustom: string = '';
  protected blockSelect: IBlockForSelect[] = [];

  /**
   * The block catalog shown in the add-block picker. Defaults to the email
   * catalog; channels with their own block set (e.g. Google RSA) pass their own.
   */
  @Input() blockCatalog: IBlockForSelect[] = blocksForSelect;

  protected variablesMaxLength: any[] = [];

  protected inputEnabled: boolean = true;

  protected contentString: string = '';
  protected isCopy: boolean = false;

  protected _destroy$ = new Subject<void>();
  protected campaignDialogOpen: boolean = false;
  protected campaignId: string | null = null;
  protected phaseId: string | null = null;

  // RCA-487: cross-channel "Search or create campaign" popover state.
  protected channelFilterChips: ChannelFilterChip[] = [];
  protected activeChannelFilter: string = ALL_CHANNELS_FILTER;
  protected channelSearchMatches: ChannelSearchMatch[] = [];
  protected channelSearchLoading = false;
  private channelSearchTerm$ = new Subject<string>();
  private lastChannelSearchTerm = '';

  constructor(
    protected templateService: TemplateService,
    protected campaignService: CampaignService,
    protected translateTemplateService: TranslateService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    protected toastBarService: ToastBarService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected accountInfoService: AccountInfoService,
    protected globalTemplateService: GlobalTemplateService,
    protected greyOverlayService: GreyOverlayService,
    protected signalRService: SignalRService,
    protected validateInputService: ValidateInputService,
    protected _variableMaxLengthService: VariableMaxLengthService,
    protected searchTemplateService: SearchTemplateService,
    protected translateService: TranslateService,
    protected responsysService: ResponsysService,
    protected dialog: MatDialog,
    protected blockManagementService: BlockManagementService,
    protected mediaManagementService: MediaManagementService,
    protected templateExportService: TemplateExportService,
    protected whatsappService: WhatsappService
  ) {
    if (this.isNew) {
      this.setRedirectData();
    }

    this.clickSubject
      .pipe(takeUntil(this._destroy$))
      .pipe(debounceTime(3000))
      .subscribe(() => {
        this.isSubmitDisabled = false; // re-enable button after debounce time
      });
  }

  ngOnInit(): void {
    // blockCatalog is now an @Input(), so it is only populated by the time
    // ngOnInit runs (not in the constructor).
    this.blockSelect = this.blockCatalog;

    this.onInitComponentSpecificBehaviour();
    this.subscribeToInputValidation();
    this.subscribeToVariableMaxLength();
    this.subscribeToTemplateSuggestions();
    this.subscribeToChannelSearch();
    this.subscribeToSignalRListeners();
    this.initializeTemplateTypeData();

    this.initalizeComponentSpecificSubscriptions();

    this.checkRouteParams();

    this.fetchDataOnRoute();

    this.setUserData();

    // Set current translator state
    this.setupTranslatorMode();

  }

  private initializeTemplateTypeData() {
    this.globalTemplateService.setTemplateTypeData(this.templateType);
  }

  ngAfterViewChecked(): void {
    if (this.viewMode == this.READONLY) {
      // disable editor
      this.disableEditor(true);
    }

    this.removeElementsIfTranslator(['.remove-block-icon']);
  }

  ngOnDestroy(): void {
    this.templateName = '';
    // clear the states from the service
    this.validateInputService.unregister();
    // Used for unsubscribing
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Subscribes to staged-media data for channels that use media
   * (driven by the `subscribeToMedia` input).
   */
  protected initalizeComponentSpecificSubscriptions(): void {
    if (this.subscribeToMedia) {
      this.subscribeToMediaData();
    }
  }

  /**
   * Inheriting components can setup specific behaviour.
   */
  protected onInitComponentSpecificBehaviour(): void {
    return;
  }

  /**
   * Handles the block change event from add block component
   * @param blockChangeEvent - holds the value of the selected block and the index where new block should be inserted
   */
  protected handleBlockChangeEvent(blockChangeEvent: any) {
    const block = blockChangeEvent.selectedBlock;
    const spliceIndex = blockChangeEvent.spliceIndex;
    this.handleBlockClick(block, spliceIndex);
  }

  /**
   * Hides specific UI elements from the DOM based on the user's translator status.
   *
   * If the current user is a translator (`this.isTranslator` is true), this method
   * iterates over a predefined list of CSS selectors and sets the `display` style
   * property of all matching elements to `'none'`, effectively hiding them from view.
   * This is typically used to prevent translators from interacting with certain
   * elements, such as block removal icons.
   *
   * @protected
   */
  protected removeElements(forbiddenElements: Array<string>) {
    // Remove elements that shouldn't be visible, making this an array in case anyone wants to disable other elements
    forbiddenElements.forEach((selector: string) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        (element as HTMLElement).style.display = 'none';
      });
    });
  }

  /**
   * Hides specific UI elements from the DOM based on the user's translator status.
   *
   * If the current user is a translator (`this.isTranslator` is true), this method
   * iterates over a predefined list of CSS selectors and sets the `display` style
   * property of all matching elements to `'none'`, effectively hiding them from view.
   * This is typically used to prevent translators from interacting with certain
   * elements, such as block removal icons.
   *
   * @protected
   */
  protected removeElementsIfTranslator(forbiddenElements: [string]) {
    if (this.isTranslator) {
      // Remove elements that shouldn't be visible, making this an array in case anyone wants to disable other elements
      forbiddenElements.forEach((selector: string) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          (element as HTMLElement).style.display = 'none';
        });
      });
    }
  }

  /**
   * When user is routed to this page, this function fetches the template data on init.
   */
  protected fetchDataOnRoute() {
    if (!this.templateName) {
      this.toogleDisableEdit(this.READONLY);
    }
    // Fetch template data
    this.route.params
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.templateName) {
          this.fetchTemplateData();
        }
      }
      );

  }

  /**
   * Set up translator functionalities.
   */
  protected setupTranslatorMode() {
    this.translateService.setIsTranslator(this.isTranslator);

    if (this.isTranslator && this.templateName !== null) {
      this.translateService
        .getTemplateStatus(this.templateName!, this.selectedLanguage)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res: TranslatingTemplateStatus) => {
            this.isTranslated = res.translated;
            this.toogleDisableEdit(this.READONLY);
          },
          error: (err) => {
            console.error('Error while fetching the template status:', err);
            this.toastBarService.showToast({
              severity: 'error',
              summary: 'Error',
              detail: `Error while fetching the template status!`,
            });
          },
        });
    } else if (this.templateName) {
      this.setIsBeingTranslated();
      this.checkIfAlreadyApproved();
    } else {
      this.isTranslated = false;
    }
  }

  private setIsBeingTranslated() {
    this.translateService
      .getTemplatesByTemplateName(this.templateName!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (existingTemplates: TranslatingTemplateStatus[]) => {
          if (existingTemplates != null && existingTemplates.length > 0) {
            this.isBeingTranslated = true;
          } else {
            this.isBeingTranslated = false;
          }
        },
        error: (err) => {
          console.error(
            'Something went wrong while checking for existing template!',
            err
          );
        },
      });
  }

  protected subscribeToMediaData() {
    this.globalTemplateService.temporaryMediaData$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.temporaryMedia = res;
        },
      });

    // Subscribe to media for deletion array
    this.globalTemplateService.mediaForDeletionWithinABlock$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.mediaForDeletionWithinABlock = res;
        },
      });

    // Subscribe to media for deletion array
    this.globalTemplateService.mediaForDeletion$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.mediaForDeletion = res;
        },
      });
  }

  protected subscribeToVariableMaxLength() {
    this._variableMaxLengthService.variablesData$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.variablesMaxLength = res;
        },
      });
  }

  protected subscribeToInputValidation() {
    this.validateInputService.inputEnabled$
      .pipe(takeUntil(this._destroy$))
      .subscribe((response: any) => {
        this.inputEnabled = response;
      });
  }

  protected subscribeToTemplateSuggestions() {
    this.searchTemplateService.searchAtTemplate$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: string[]) => {
          // Populate the suggestions array
          this.suggestions = res
            ? res.map((template) => ({
              name: template,
              hint: template,
            }))
            : undefined;

          // If no template is found, add an option to create the table with this name
          if (this.suggestions?.length == 0) {
            this.templateName! = Helper.removeSpecialCharacters(
              this.templateName!,
              this.templateType
            );
            this.suggestions[0] = {
              name: this.templateName!,
              hint: `No template found! Create a template with "${this.templateName}" name?`,
            };
            this.setIsNew(true);
            this.isSubmitDisabled = false;
            this.toogleDisableEdit(this.EDIT);
            const input = `${this.templateName}-${this.selectedLanguage}`;
            this.idcustom = Helper.generateIdHash(input);
          } else if (!this.suggestions?.find((s) => s.name === this.templateName!)) {
            this.suggestions?.push({
              name: this.templateName!,
              hint: `No template found! Create a template with "${this
                .templateName!}" name?`,
            });
            this.setIsNew(true);
            this.isSubmitDisabled = false;
            this.toogleDisableEdit(this.EDIT);
            const input = `${this.templateName}-${this.selectedLanguage}`;
            this.idcustom = Helper.generateIdHash(input);
          }
        },
        error: (err) => {
          console.warn('Search template service returned an error', err);
        },
      });
  }

  /**
   * RCA-487: cross-channel "Search or create campaign" popover. Debounces the
   * typed term, re-runs on every keystroke and on channel-chip changes, and
   * always refreshes `channelFilterChips`' counts from the same response so
   * the chips reflect the current search term regardless of which one is active.
   */
  private subscribeToChannelSearch(): void {
    this.channelSearchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.channelSearchLoading = true;
          const filterType = this.activeChannelFilter === ALL_CHANNELS_FILTER
            ? undefined
            : (this.activeChannelFilter as TypeEnum);
          return this.templateService.searchTemplatesAcrossChannels(term, filterType).pipe(
            catchError((err) => {
              console.error('Cross-channel search failed', err);
              return of({ channelCounts: {}, matches: [] } as ChannelSearchResult);
            })
          );
        }),
        takeUntil(this._destroy$)
      )
      .subscribe((result) => {
        this.channelSearchLoading = false;
        this.applyChannelSearchResult(result);
      });
  }

  private applyChannelSearchResult(result: ChannelSearchResult): void {
    const counts = result.channelCounts ?? {};
    const total = Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0);

    this.channelFilterChips = [
      { key: ALL_CHANNELS_FILTER, label: 'All', count: total, isCurrent: false },
      ...CHANNEL_FILTER_ORDER.map((type) => ({
        key: type,
        label: CHANNEL_FILTER_LABELS[type]!,
        count: counts[type] ?? 0,
        isCurrent: type === this.templateType,
      })),
    ];

    this.channelSearchMatches = result.matches ?? [];
  }

  protected onChannelSearchOpened(): void {
    // Default the chip row to the channel currently being edited, matching
    // the mockup, and list everything in scope until the user types something.
    this.activeChannelFilter = this.templateType;
    this.lastChannelSearchTerm = '';
    this.channelSearchTerm$.next('');
  }

  protected onChannelSearchTermChange(term: string): void {
    this.lastChannelSearchTerm = term;
    this.channelSearchTerm$.next(term);
  }

  protected onChannelSearchFilterChange(key: string): void {
    this.activeChannelFilter = key;
    // Re-run with whatever the user already typed, not a fresh empty search.
    this.channelSearchTerm$.next(this.lastChannelSearchTerm);
  }

  protected onChannelSearchResultClick(match: ChannelSearchMatch): void {
    if (match.templateType === this.templateType) {
      // Same channel as the editor that's already open: fill the template-name
      // field and render in place, exactly like picking a suggestion from the
      // regular same-channel search (setTemplateName -> onSubmitTemplateName).
      this.templateName = match.templateName;
      this.onSubmitTemplateName();
      return;
    }

    // A different channel has its own editor route/component entirely, so
    // this one does need a real navigation.
    const basePath = CHANNEL_ROUTE_BASE_PATHS[match.templateType];
    if (!basePath) {
      console.warn('No route configured for channel', match.templateType);
      return;
    }
    this.router.navigateByUrl(`${basePath}${encodeURIComponent(match.templateName)}`);
  }

  protected subscribeToSignalRListeners() {
    this.signalRService.addTemplateLockedByListener(
      (templateId: string, lockedBy: IUser) => {
        if (this.idcustom !== templateId) {
          return;
        }
        // console.log('lock by SIGNAL R->', lockedBy);
        this.lockedBy.name = lockedBy.name;
        this.lockedBy.username = lockedBy.username;
        if (lockedBy.username != this.currentUser?.username) {
          this.toogleDisableEdit(this.READONLY);
        } else {
          this.toogleDisableEdit(this.EDIT);
        }
      }
    );

    this.signalRService.addTemplateProgressListener(
      (templateName: string, isBeingProc: boolean) => {
        if (this.templateName !== templateName) {
          return;
        }

        this.isTemplateProcToResponsys = isBeingProc;
        if (isBeingProc) {
          this.toogleDisableEdit(this.EDIT);
          this.removeElements(['.comments-side']);
        }
      }
    );

    this.signalRService.addTemplateDeletionListener(
      (templateName: string, isDeleted: boolean) => {
        if (this.templateName !== templateName) {
          return;
        } else if (isDeleted) {
          this.clearTemplateData();
          this.toastBarService.showToastSuccess(
            'Template has been successfully deleted!'
          );
        }
      }
    );
  }

  /**
   * Checks if the template was once already translated, if it was, disable the Translate button.
   */
  protected checkIfAlreadyApproved() {
    this.translateService
      .checkIfApprovedTemplate(this.templateName!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (isApproved) => {
          if (isApproved) {
            this.isApproved = isApproved;
          }
        },
        error: (error) => {
          console.error(
            `Error while checking approval status of ${this.templateName!}!`,
            error
          );
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `Error while checking approval status of ${this
              .templateName!}!`,
          });
        },
      });
  }


  protected outputTextContent(content: TemplateRef<any>): void {
    // open modal
    this.outputModal = this.modalService.open(content, {
      fullscreen: true,
    });

    this.contentString = this.templateExportService.generateContentString(this.selectedBlocks, this.variablesMaxLength);
  }

  protected setUserData() {
    this.accountInfoService.accountInfo$
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        this.currentUser = data;
      });
  }

  protected drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.selectedBlocks,
      event.previousIndex,
      event.currentIndex
    );
    // set reorder to true
    this.reorder = true;
  }

  protected toogleDisableEdit(eventValue: string) {
    if (this.viewMode == eventValue) {
      return;
    }
    this.viewMode = eventValue;

    if (eventValue == this.EDIT) {
      this.viewMode = this.EDIT;
      this.globalTemplateService.setIsDisabled(false);

      this.disableEditor(false);
    } else {
      this.viewMode = this.READONLY;
      this.globalTemplateService.setIsDisabled(true);

      this.disableEditor(true);
    }
  }

  protected disableEditor(disable: boolean): void {
    // get all editors
    const allEditors = document.querySelectorAll('.left-side');

    if (disable) {
      // disable editor
      allEditors.forEach((editor) => {
        editor.classList.add('disable-editor');
      });
    } else {
      // enable editor
      allEditors.forEach((editor) => {
        editor.classList.remove('disable-editor');
      });
    }
  }

  // Method to handle check is name used
  protected onSubmitTemplateName(): void {
    if (this.templateName) {
      // enable grey overlay
      this.greyOverlayService.enableGreyOverlay();

      this.templateName = Helper.removeSpecialCharacters(this.templateName, this.templateType);
      const input = `${this.templateName}-${this.selectedLanguage}`;
      this.idcustom = Helper.generateIdHash(input);
      this.updateTemplateId(this.idcustom);
      this.lockedBy.username = '';
      this.lockedBy.name = '';
      if (this.selectedBlocks.length != 0) this.selectedBlocks = [];
      this.setTemplateData();
    }
  }

  // Method to handle check is name used
  protected clearTemplateData(): void {
    if (this.templateName) {
      this.lockedBy.username = '';
      this.lockedBy.name = '';
      this.selectedBlocks = [];
      this.templateName = '';
      this.idcustom = '';
      this.router.navigate([this.templateBasePath]);
    }
  }

  protected updateTemplateId(id: string): void {
    this.globalTemplateService.setTemplateId(id);
    this.globalTemplateService.setTemplateIdData(id);
  }

  protected confirmTableDeletion(content: TemplateRef<any>) {
    // check menu status
    this.toogleDisableEdit(this.EDIT);

    this.activeModal = this.modalService.open(content, {
      centered: true,
      backdrop: 'static',
    });
  }

  protected deleteTable() {
    // close modal
    this.activeModal.close();

    // enable grey overlay
    this.greyOverlayService.enableGreyOverlay();
    this.templateService
      .deleteTemplate(this.templateName)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.greyOverlayService.disableGreyOverlay();
          this.isTemplateProcToResponsys = true;
          this.toogleDisableEdit(this.EDIT);
          this.disableEditor(true);
          this.removeElements(['.comments-side']);
        })
      )
      .subscribe({
        next: () => {
          this.toastBarService.showToastSuccess(
            'Supplemental table delete request accepted!'
          );
        },
        error: (err) => {
          this.toastBarService.showToastError(
            `Supplemental table wasn't deleted!`
          );
          console.error(`Supplemental table wasn't deleted!`, err);
        },
      });
  }

  protected activateMenuStatusModal(): void {
    this.toogleDisableEdit(this.EDIT);
    // close modal
    this.activeModal.close();
    const blockMenuStatus = document.getElementById('annotate-menu');
    // reactivate menu
    if (blockMenuStatus) blockMenuStatus.style.display = 'block';
  }

  protected setTemplateData() {
    // check menu status
    if (!this.templateName) return;

    if (this.isTranslator) {
      this.translateTemplateService
        .getTemplate({
          templateName: this.templateName!,
          language: this.selectedLanguage ?? 'en',
          templateType: this.templateType
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res) => {
            this.globalTemplateService.setTemplateNameData(
              this.templateName ?? 'null'
            );
            this.setupOldTemplate(res);
          },
        });
    } else {
      // Route to new selection
      this.router.navigate([`${this.templateBasePath}${this.templateName!}/${this.selectedLanguage}`]);
    }
  }

  protected fetchTemplateData() {
    this.templateService
      .getTemplate({
        templateName: this.templateName!,
        language: this.selectedLanguage ?? 'en',
        templateType: this.templateType
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: any) => {
          this.handleTemplateDataResponse(res);
        },
        complete: () => {
          // disable grey overlay once the template has been populated
          this.handleTemplateDataComplete();
        },
        error: (error: any) => {
          this.handleTemplateDataError(error);
        },
      });
  }

  protected handleTemplateDataError(error: HttpErrorResponse) {

    // Template name exists for some type
    if (error.status == 409) {
      this.toastBarService.showToastError(error.error.detail);
    } else {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: `${error.message}`,
      });
    }

    this.setIsNew(true);
    this.globalTemplateService.setIsNewTemplateData(true);
    this.templateName = '';
    this.greyOverlayService.disableGreyOverlay();
  }

  protected handleTemplateDataComplete() {
    this.greyOverlayService.disableGreyOverlay();
  }

  protected handleTemplateDataResponse(res: any) {
    this.globalTemplateService.setTemplateNameData(
      this.templateName ?? 'null'
    );

    if (res == null) {
      this.handleNewTemplate();
    } else {
      this.setupOldTemplate(res);
    }
  }

  protected handleNewTemplate() {
    this.toastBarService.showToast({
      severity: 'info',
      summary: 'Info',
      detail: `Select a block and press "Create template" to create: ${this.templateName}`,
    });
    this.markTemplateAsNew();
    this.selectedBlocks = [];
    // add default subject and preheader
    this.initializeDefaultBlocks();

    if (this.viewMode != this.EDIT) {
      this.toogleDisableEdit(this.EDIT);
    }

    this.generateIdCustom();
  }

  protected generateIdCustom() {
    const input = `${this.templateName}-${this.selectedLanguage}`;
    this.idcustom = Helper.generateIdHash(input);
  }

  protected initializeDefaultBlocks() {
    if (this.selectedBlocks.length !== 0) {
      return;
    }
    // Track the running instance index per block name so multiple blocks of the
    // same name (e.g. several sitelinks) get distinct counts and blockIds.
    const countByName: { [name: string]: number } = {};
    this.defaultBlocks.forEach((spec) => {
      const count = spec.count ?? (countByName[spec.name] ?? 0) + 1;
      countByName[spec.name] = count;
      this.selectedBlocks.push({
        blockId: Helper.generateIdHash(
          `${this.templateName}-${this.selectedLanguage}-${spec.name}-${count}`
        ),
        name: spec.name,
        count: count,
        variables: { ...spec.variables },
      });
    });
  }

  protected markTemplateAsNew() {
    this.setIsNew(true);
    this.globalTemplateService.setIsNewTemplateData(true);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Hydrates channel-specific state from a loaded template. WhatsApp uses this to
   * pick up its Meta approval status (driven by the `loadsMetaStatus` input).
   */
  protected setComponentSpecificValues(templateData: any) {
    if (this.loadsMetaStatus) {
      this.metaStatus = templateData.metaStatus || MetaStatusEnum.NEVER_SENT;
      this.isApproved = false;
    }
  }

  /**
   * Setups the template data if the template is not new
   * @param res - Response from the backend containing the template data
   */
  setupOldTemplate(res: any) {

    this.setComponentSpecificValues(res);

    this.setIsNew(false);
    this.globalTemplateService.setIsNewTemplateData(false);
    this.selectedBlocks = res.blocks;

    this.idcustom = res.templateId;
    this.updateTemplateId(this.idcustom);
    this.lockedBy.username = res.lockedBy?.username ?? '';
    this.lockedBy.name = res.lockedBy?.name ?? '';
    this.modifiedDate = res.modifiedDate;

    // Subscribe to check if template is being processed to Responsys
    this.responsysService
      .IsTemplateProcToResponsys(this.templateName!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (isTemplateProcToResponsys: any) => {
          this.isTemplateProcToResponsys = isTemplateProcToResponsys;
          if (isTemplateProcToResponsys) {
            this.toogleDisableEdit(this.READONLY);
            this.removeElements(['.comments-side']);
          }
        },
        error: (err) => {
          console.error(
            'Error while checking if the template is being processed to Responsys!',
            err
          );
          this.toastBarService.showToastError(err);
        },
      });

    // Only the user already holding the lock gets edit access - everyone else
    // (including when nobody holds it yet, regardless of modifiedDate) stays
    // readonly until they explicitly take the lock.
    if (res.lockedBy?.username == this.currentUser?.username) {
      this.toogleDisableEdit(this.EDIT);
    } else {
      this.toogleDisableEdit(this.READONLY);
    }
  }

  protected onSubmitBlocks(isResponsysSave = false) {
    // Defense in depth: never persist changes unless this session actually holds
    // the lock. A brand-new template (isNew) has no lock yet - it's taken right
    // after the create call succeeds (see handleTemplateCreationResponse()).
    if (!this.isNew && !this.isMyLock()) {
      this.toastBarService.showToastError('Lock the template before saving!');
      return;
    }

    // When done with all other actions, update the Responsys table
    if (isResponsysSave) {
      this.isResponsysSave = true;
    }

    this.templateName = Helper.removeSpecialCharacters(this.templateName!, this.templateType);

    // Disable submit button
    this.clickSubject.next(); // emit an event to trigger debounce
    this.isSubmitDisabled = true;

    // Check all field values
    this.toogleDisableEdit(this.EDIT);

    // Highlight all blocks that have empty fields
    let noEmptyFields = this.validateInputService.highlightInvalidEditorBlocks();

    // Same-type blocks may not carry identical (normalized) text within a field group
    const duplicateFieldKeys = this.getDuplicateFieldKeys();
    const hasDuplicateText = duplicateFieldKeys.size > 0;
    if (hasDuplicateText) {
      this.validateInputService.highlightDuplicateEditorBlocks(duplicateFieldKeys);
    }

    // Media uploads and dropdowns have no editor for the empty-field check to see
    const missingRequiredFields = this.getMissingRequiredFields();
    const hasMissingRequiredFields = missingRequiredFields.length > 0;

    if (!this.inputEnabled || !noEmptyFields || hasDuplicateText || hasMissingRequiredFields) {
      // Validation failed
      if (hasDuplicateText) {
        this.toastBarService.showToastError('Duplicate text found');
      }
      if (hasMissingRequiredFields) {
        this.toastBarService.showToastError(`Required: ${missingRequiredFields.join(', ')}`);
      }
      return this.handleValidationErrors(noEmptyFields);
    }

    // Validation passed - only now check/ask for a campaign (RCA-480), so we
    // never create a campaign for a save that was going to fail anyway.
    this.ensureTemplateHasCampaign().subscribe((canProceed) => {
      if (canProceed) {
        this.continueSubmitBlocks();
      } else {
        // Cancelled the campaign prompt - re-enable the form, nothing to persist.
        this.isSubmitDisabled = false;
      }
    });
  }

  /**
   * RCA-480: a template must belong to at least one campaign before it can be
   * saved. Emits true once that's satisfied (already connected, or just
   * connected via the name-campaign prompt), or false if the user cancelled
   * the prompt - in which case the caller must abort the save entirely,
   * matching the mockup's Cancel/X behavior (nothing gets persisted).
   */
  private ensureTemplateHasCampaign(): Observable<boolean> {
    // A brand-new template created from within a campaign/phase context
    // (campaignId arrives via query params - see checkRouteParams()) already
    // satisfies this; the existing create-response handler connects it.
    if (this.isNew) {
      if (this.campaignId) {
        return of(true);
      }
      return this.promptForNewCampaign();
    }

    // Existing template: ask the backend, since campaignId being empty here
    // only reflects how THIS session was opened, not whether a connection
    // was made earlier (e.g. via the "Connect to Campaign" dialog).
    return this.campaignService.getCampaignListForTemplate(this.idcustom).pipe(
      switchMap((campaigns) => {
        if (campaigns && campaigns.length > 0) {
          return of(true);
        }
        return this.promptForNewCampaign();
      }),
      catchError((err) => {
        console.error('Failed to check campaign connections for template', err);
        // Don't block saving just because the check itself failed.
        return of(true);
      })
    );
  }

  private promptForNewCampaign(): Observable<boolean> {
    const dialogRef = this.dialog.open(NameCampaignDialogComponent, {
      width: '420px',
      data: { channelLabel: this.channelLabel },
    });

    return dialogRef.afterClosed().pipe(
      switchMap((result: NameCampaignDialogResult | undefined) => {
        if (!result) {
          return of(false);
        }

        const userInfo = {
          name: this.currentUser?.name,
          username: this.currentUser?.username,
        };

        // The dialog offers existing campaigns by name (autocomplete) before
        // falling back to creating a new one, so a name that already exists
        // reuses that campaign instead of creating a duplicate.
        const campaign$: Observable<ICampaign> = result.campaignId
          ? of({ campaignId: result.campaignId, campaignName: result.campaignName, phases: result.phases })
          : this.campaignService.createCampaign({
            campaignName: result.campaignName,
            userInfo,
            // A channel only really counts as connected to a campaign once
            // it's connected to one of its phases - connectTemplateToCampaigns
            // is deprecated and doesn't actually complete the connection on
            // its own. Ask for a first phase right away so there's one to
            // connect to immediately.
            initialPhases: [{ phaseName: 'Phase 1', phaseOrder: 1, userInfo }],
          });

        return campaign$.pipe(
          switchMap((campaign) => {
            this.campaignId = campaign.campaignId ?? null;
            if (!this.campaignId) {
              console.error('Campaign has no id', campaign);
              this.toastBarService.showToastError('Failed to create campaign');
              return of(false);
            }

            // Always use the LAST phase: for a campaign we just created
            // that's the single "Phase 1" above; for an existing campaign
            // picked from the autocomplete, the last one is still the right
            // target. If an existing campaign somehow has none yet, create one.
            const phases = campaign.phases ?? [];
            const lastPhase = phases.length > 0 ? phases[phases.length - 1] : null;
            const phase$: Observable<ICampaignPhase> = lastPhase
              ? of(lastPhase)
              : this.campaignService.createPhase(this.campaignId, { phaseName: 'Phase 1', phaseOrder: 1, userInfo });

            return phase$.pipe(
              switchMap((phase) => {
                this.phaseId = phase.phaseId ?? null;
                if (!this.phaseId) {
                  console.error('Campaign has no usable phase', campaign);
                  this.toastBarService.showToastError('Failed to create campaign');
                  return of(false);
                }

                // New templates get connected automatically once campaignId
                // and phaseId are set (see handleTemplateCreationResponse()).
                // An existing template needs the connection made right now.
                if (!this.isNew) {
                  return this.campaignService.editTemplateToPhaseConnections({
                    templateId: this.idcustom,
                    campaignId: this.campaignId!,
                    selectedPhaseIds: [this.phaseId],
                    templateType: this.templateType.toString(),
                  }).pipe(
                    switchMap(() => of(true))
                  );
                }

                return of(true);
              })
            );
          }),
          catchError((err) => {
            console.error('Failed to create campaign', err);
            this.toastBarService.showToastError('Failed to create campaign');
            return of(false);
          })
        );
      })
    );
  }

  private continueSubmitBlocks() {
    console.log('submitting blocks campaignId', this.campaignId);
    this.greyOverlayService.enableGreyOverlay();

    if (this.isCopy) {
      this.resetIds();
    }

    // Reorder the counts in blocks
    this.blockManagementService.reorderCount(this.selectedBlocks);

    // check whether its an update or create
    if (this.isNew) {
      this.createTemplate();
      return;
    }

    // Check if there is any media in queue for deletion within a block before updating the template
    if (
      this.shouldHandleMediaDeletion()
    ) {
      this.checkIfMediaNeedsDeletion();
      return;
    }

    // Handle if its a translate templates
    if (this.isTranslator) {
      this.handleTranslateUpdate();
    } else {
      this.updateTemplate();
    }
  }

  protected shouldHandleMediaDeletion() {
    return this.supportsMediaDeletion && (
      this.mediaForDeletion.length > 0 ||
      this.mediaForDeletionWithinABlock.length > 0
    );
  }

  private handleTranslateUpdate() {
    this.translateTemplateService
      .updateTemplate({
        templateName: this.templateName!,
        idcustom: this.idcustom,
        language: this.selectedLanguage,
        templateData: this.selectedBlocks,
        reorder: false,
        lockedBy: {
          username: this.currentUser?.username,
          name: this.currentUser?.name,
        },
        templateType: this.templateType
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.handleSuccessfulTemplateUpdate(res);
        },
      });
  }

  private handleValidationErrors(noEmptyFields: boolean) {
    if (!this.inputEnabled) {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Some fields have too many characters!',
      });
    }

    if (!noEmptyFields) {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Some fields are empty!',
      });
    }

    this.isSubmitDisabled = false; // Re-enable if validation fails
    this.greyOverlayService.disableGreyOverlay();
    return;
  }

  protected saveAsEmpty() {
    let selectedBlocsCopy = this.selectedBlocks;
    selectedBlocsCopy.forEach((block: BlockModel) => {
      if (block.variables) {
        Object.keys(block.variables).forEach((k) => {
          block.variables![k] = "";
        });
      } else {
        block.variables = {};
      }
    })
    this.templateService
      .createTemplate({
        templateName: this.templateName + "_empty"!,
        idcustom: this.idcustom,
        language: this.selectedLanguage,
        templateData: selectedBlocsCopy,
        templateType: this.templateType
      }).pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Template successfully created as empty!',
          });
        },
        error: (error) => {
          console.error(
            'Something went wrong with creating the template!',
            error
          );
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong with creating the template!',
          });
        }
      });
  }

  /**
   * Checks if there is any media marked for deletion, either within a block or globally,
   * and triggers their deletion by subscribing to the corresponding observables.
   *
   * - If both block-specific and global media deletion observables exist, waits for both to complete together.
   * - If only one exists, subscribes to it individually.
   * - After successful deletion, clears the deletion list and updates the template.
   * - On error, logs the warning and updates the template.
   *
   * @protected
   */
  protected checkIfMediaNeedsDeletion() {

    // Arrays which hold the observables and their function which populates them
    let mediaForDeletionWithinABlockObservables: Array<Observable<string>> =
      [];
    let mediaForDeletionObservables: Array<Observable<string>> = [];

    // Populate the observables with media
    this.mediaManagementService.populateMediaForDeletionWithinABlockObservables(
      mediaForDeletionObservables,
      this.mediaForDeletionWithinABlock,
      this.templateName!
    );
    this.mediaManagementService.populateMediaForDeletionObservables(mediaForDeletionObservables, this.mediaForDeletion);

    // Create forks for media deletion
    const {
      deleteAllMediaWithinABlock$,
      deleteAllMedia$
    } = this.mediaManagementService.generateMediaDeletionForks(mediaForDeletionWithinABlockObservables, mediaForDeletionObservables);

    if (deleteAllMediaWithinABlock$ && deleteAllMedia$) {
      // Both present: wait for both to complete together
      forkJoin({
        block: deleteAllMediaWithinABlock$,
        other: deleteAllMedia$,
      })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: ({ }) => {
            this.handleMediaDeletionResponse();
          },
          error: (err) => {
            this.handleMediaDeletionError(err);
          },
        });
    } else {
      this.handleIndividualMediaDeletion(deleteAllMediaWithinABlock$, deleteAllMedia$);
    }
  }

  /**
   * When only one of these two: `deleteAllMediaWithinABlock$` and `deleteAllMedia$` are not empty, handle media deletion for the one that is not empty.
   * @param deleteAllMediaWithinABlock$
   * @param deleteAllMedia$
   */
  private handleIndividualMediaDeletion(deleteAllMediaWithinABlock$: Observable<string[]> | undefined, deleteAllMedia$: Observable<string[]> | undefined) {
    deleteAllMediaWithinABlock$?.pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.handleMediaDeletionResponse();
      },
      error: (err) => {
        this.handleMediaDeletionError(err);
      },
    });
    deleteAllMedia$?.pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.handleMediaDeletionResponse();
      },
      error: (err) => {
        console.warn(err);

        // Continue with updating the template
        this.handleMediaDeletionError(err);
      },
    });
  }

  private handleMediaDeletionError(err: any) {
    console.warn(err);

    // Continue with updating the template
    this.updateTemplate();
  }

  private handleMediaDeletionResponse() {
    this.globalTemplateService.clearMediaForDeletion();

    // Continue with updating the template
    this.updateTemplate();
  }

  protected sendLockedBy(user?: any) {
    let lockByUser: IUser = {
      name: '',
      username: '',
    };
    if (user) {
      lockByUser.name = user.name;
      lockByUser.username = user.username;
      this.toogleDisableEdit(this.EDIT);
    } else {
      this.toogleDisableEdit(this.READONLY);
    }
    this.templateService
      .sendLockBy(this.idcustom, lockByUser)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          // console.log('Change lock by response-> ', response);
          if (response.modifiedCount && response.modifiedCount == 1)
            this.lockedBy.username = lockByUser?.username ?? '';
          this.lockedBy.name = lockByUser?.name ?? '';
        },
        error: (error) => {
          console.error('Change lock by error: ', error);
        },
      });
    // console.log('lockby2-> ', this.lockedBy);
  }

  protected createTemplate() {
    // If there is media that is locally saved and the template is new,
    // create an observables array which holds the media that is to be uploaded
    if (this.temporaryMedia.length > 0 && this.isNew) {

      const uploadMediaObservables = this.mediaManagementService.createMediaObservablesArray(this.selectedBlocks, this.temporaryMedia, this.templateName!);

      // Check one more time if the template even contains any media
      this.checkMediaQueue(uploadMediaObservables);
    }
    // If there is no media in the template
    else {
      this.continueTemplateCreation();
    }
  }

  private checkMediaQueue(uploadMediaObservables: Observable<string>[]) {
    if (uploadMediaObservables.length > 0) {
      forkJoin(uploadMediaObservables)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            // Send the new temporary media array with added new media URIs
            // after the media has been uploaded
            this.globalTemplateService.setNewTemporaryMediaData(
              this.temporaryMedia
            );
            this.continueTemplateCreation();
          },
          error: (err) => {
            console.error('One or more media uploads failed', err);
            this.toastBarService.showToastError(
              getUploadErrorMessage(err, 'Upload failed, the template was not saved.')
            );
          },
        });
    }
  }

  /**
   * Initiates the template creation process by sending the current template data to the backend service.
   * Displays a toast notification based on the success or failure of the operation.
   * On success, updates the state to indicate the template is no longer new and notifies relevant services.
   * On error, displays an error toast with the error code.
   * Always disables the grey overlay upon completion.
   *
   * @protected
   */
  protected continueTemplateCreation() {
    console.log("this template name ", this.templateName);
    console.log("Template lang", this.selectedLanguage)
    console.log("this.idcustom", this.idcustom);
    console.log("HASH:", Helper.generateIdHash(
      `${this.templateName}-${this.selectedLanguage}`))

    this.idcustom = Helper.generateIdHash(
      `${this.templateName}-${this.selectedLanguage}`
    );

    console.log("this.idcustom-new", this.idcustom);

    if (this.isCopy) {
      this.templateService
        .copyTemplate(
          {
            templateName: this.templateName!,
            idcustom: this.idcustom,
            language: this.selectedLanguage,
            templateData: this.selectedBlocks,
            templateType: this.templateType
          },
          this.originalTemplateName!
        )
        .pipe(
          finalize(() => {
            // Re-enable the screen
            this.greyOverlayService.disableGreyOverlay();
          })
        )
        .subscribe({
          next: async () => {
            this.toastBarService.showToast({
              severity: 'success',
              summary: 'Success',
              detail: 'Template succesfully copied!',
            });
            this.setIsNew(false);
            this.isCopy = false;
            this.globalTemplateService.setIsNewTemplateData(false);
            this.sendLockedBy(this.currentUser);
            await this.router.navigate([`${this.templateBasePath}${this.templateName!}`]);
            this.reloadPageClean();
          },
          error: (error) => {
            this.handleTemplateCopyError(error);
          },
        });
    } else {
      this.templateService
        .createTemplate({
          templateName: this.templateName!,
          idcustom: this.idcustom,
          language: this.selectedLanguage,
          templateData: this.selectedBlocks,
          templateType: this.templateType
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            this.handleTemplateCreationResponse();
            if (this.campaignId) {///ako je create došao u kontextu kampanje ,poveži ih odmah
              const params: ConnectTemplateToCampaignsRequest = {
                templateId: this.idcustom,
                selectedCampaignIds: [this.idcustom],
                templateType: TypeEnum.EMAIL,
              }
              this.campaignService.connectTemplateToCampaigns(params)
            }
          },

          error: (error) => {
            this.handleTemplateCreationError(error);
          },
          complete: () => {
            // disable add new block
            this.greyOverlayService.disableGreyOverlay();
          },
        });
    }
  }

  private handleTemplateCreationError(error: HttpErrorResponse) {
    console.error(
      'Something went wrong with creating the template!',
      error
    );

    // Remove loading state
    this.greyOverlayService.disableGreyOverlay();

    // Show actual backend error message
    const errorMessage = error.error?.detail || error.message || 'Something went wrong with creating the template!';
    this.toastBarService.showToast({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
    });
  }

  protected handleTemplateCreationResponse() {
    this.toastBarService.showToast({
      severity: 'success',
      summary: 'Success',
      detail: 'Template succesfully created!',
    });
    this.setIsNew(false);
    this.isCopy = false;
    this.globalTemplateService.setIsNewTemplateData(false);
    this.sendLockedBy(this.currentUser);

    // Connect to campaign if campaignId is present (old behavior, kept for backward compatibility)
    if (this.campaignId && !this.phaseId) {
      this.campaignService.connectTemplateToCampaigns({
        templateId: this.idcustom,
        selectedCampaignIds: [this.campaignId],
        templateType: this.templateType
      }).subscribe({
        next: (res: any) => {
          console.log("Connected to campaign successfully!", res);
        },
        error: (error) => {
          console.error('Connecting to campaign failed', error);
        }
      });
    }

    // Connect to phase if both campaignId and phaseId are present (new behavior)
    if (this.campaignId && this.phaseId) {
      this.campaignService.editTemplateToPhaseConnections({
        templateId: this.idcustom,
        campaignId: this.campaignId,
        selectedPhaseIds: [this.phaseId],
        templateType: this.templateType.toString()
      }).subscribe({
        next: (res: any) => {
          console.log("Connected to phase successfully!", res);
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Connected',
            detail: 'Template connected to phase successfully!',
          });
        },
        error: (error) => {
          console.error('Connecting to phase failed', error);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to connect template to phase.',
          });
        }
      });
    }
  }

  private handleTemplateCopyError(error: any) {
    console.error(
      'Something went wrong with copying the template!',
      error
    );
    if (error.status === 409) {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Template with that name already exists!',
      });
    } else {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong with copying the template!',
      });
    }
  }

  protected updateTemplate() {
    this.templateService
      .updateTemplate({
        templateName: this.templateName!,
        idcustom: this.idcustom,
        language: this.selectedLanguage,
        templateData: this.selectedBlocks,
        reorder: this.reorder,
        lockedBy: {
          username: this.currentUser?.username,
          name: this.currentUser?.name,
        },
        templateType: this.templateType
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.handleSuccessfulTemplateUpdate(response);
        },
        error: (error) => {
          console.error('Update error: ', error);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: error.status.toString(),
          });
          this.greyOverlayService.disableGreyOverlay();
        },
      });
  }

  protected handleSuccessfulTemplateUpdate(response: any) {
    this.toastBarService.showToast({
      severity: 'success',
      summary: 'Success',
      detail: 'Successfully updated the template in the database!',
    });
    this.setIsNew(false);
    this.globalTemplateService.setIsNewTemplateData(false);
    this.sendLockedBy();
    this.greyOverlayService.disableGreyOverlay();

    if (this.isResponsysSave) {
      this.saveToResponsys();
    }
  }

  protected onBlockDataUpdated(data: any) {
    const blockIndex = this.selectedBlocks.findIndex(
      (block) => block.blockId === data.blockId
    );
    if (blockIndex === -1) {
      console.warn('Block with blockId not found:', data.blockId);
      return;
    }
    // Direct mutation instead of creating a new object with spread
    Object.assign(this.selectedBlocks[blockIndex], data);
  }

  protected removeBlock(blockId: any): void {

    if (!this.isBlockTresholdSatisfied(blockId)) {
      return;
    }

    // Deletes the media only if the template is later saved
    if (!this.isNew) {
      this.globalTemplateService.setMediaForDeletionWithinABlockData(blockId);
    }

    const removedBlock = this.selectedBlocks.find(
      (block) => block.blockId === blockId
    );
    const blockName = removedBlock?.name;
    this.selectedBlocks = this.selectedBlocks.filter(
      (block) => block.blockId !== blockId
    );
    if (blockName) {
      const blocksToReorder = this.selectedBlocks.filter(
        (block) => block.name === blockName
      );
      blocksToReorder.forEach((block, index) => {
        block.count = index + 1;
      });
    }

    this.toastBarService.showToast({
      severity: 'success',
      summary: 'Success',
      detail: 'Successfully deleted a block!',
    });
    if (!this.isNew) this.reorder = true;
  }

  /**
   * Inserts blocks to selectedBlocks array
   * @param block - new block for insertion
   * @param place - default -1 for adding new blocks at the end of the array
   */
  protected handleBlockClick(block: any, place = -1) {
    const name = block.name;

    if (this.isBlockLimitReached(name)) {
      return;
    }

    console.log(`[handleBlockClick] Adding block: ${name}`);
    console.log(`[handleBlockClick] Current blocks with same name:`, this.selectedBlocks.filter(b => b.name === name).map(b => ({ count: b.count, blockId: b.blockId })));

    // Find the highest count for blocks with the same name
    const maxCount = Math.max(0, ...this.selectedBlocks
      .filter(b => b.name === name)
      .map(b => b.count || 0));

    const newCount = maxCount + 1;

    console.log(`[handleBlockClick] maxCount: ${maxCount}, newCount: ${newCount}`);

    // Add timestamp and random to ensure uniqueness even if count is the same
    const blockId = Helper.generateIdHash(
      `${this.templateName}-${this.selectedLanguage}-${name}-${newCount}-${Date.now()}-${Math.random()}`
    );

    console.log(`[handleBlockClick] Generated blockId: ${blockId}`);

    const blockObject: BlockModel = {
      blockId: blockId,
      name: name,
      count: newCount,
      variables: {},
    };

    // Add a block between other block
    if (place !== -1) {
      this.selectedBlocks.splice(place, 0, blockObject);
      if (!this.isNew) this.reorder = true;

      // Push the new block at the end of the array
    } else if (this.templateType === TypeEnum.GOOGLE_RSA_SEARCH) {
      const placeIndex = this.selectedBlocks.findIndex(b => b.count === maxCount);
      this.selectedBlocks.splice(placeIndex + 1, 0, blockObject);
    }

    else {
      this.selectedBlocks.push(blockObject);
      if (!this.isNew) this.reorder = true;
    }
  }

  /**
   * Returns true (and shows an error toast) when adding another block of the
   * given name would exceed its configured limit. Used as a guard so the
   * caller can early-return.
   */
  private isBlockLimitReached(name: any): boolean {
    const limit = this.blockMaximumCount.find(b => b.blockName === name)?.limit;
    const existingBlocks = this.selectedBlocks.filter(b => b.name === name);

    if (limit !== undefined && existingBlocks.length >= limit) {
      this.toastBarService.showToastError(`Can't add this element, reached the limit of elements with this block which is ${limit}`);
      return true;
    }

    return false;
  }

  /**
 * Returns true (and shows an error toast) when removing block if the
 * given name would not satisfy its configured treshold. Used as a guard so the
 * caller can early-return.
 */
  private isBlockTresholdSatisfied(blockId: string): boolean {
    const name = this.selectedBlocks.find(b => b.blockId === blockId)?.name!;

    const treshold = this.blockMinimumCount.find(b => b.blockName === name)?.treshold;
    const existingBlocks = this.selectedBlocks.filter(b => b.name === name);

    if (treshold !== undefined && existingBlocks.length === treshold) {
      this.toastBarService.showToastError(`Can't remove this element, template needs to have atleast ${treshold} blocks of this type!`);
      return false;
    }

    return true;
  }

  /**
   * Enforces `uniqueTextConstraints`: within each block type, the configured
   * field group's values (pooled across all blocks of that type) must be
   * distinct after normalization. Returns the `${blockId}::${fieldName}` key of
   * every offending editor (empty when everything is unique) so they can be
   * outlined. Empty values are ignored here — they are handled by the
   * empty-field validator.
   */
  private getDuplicateFieldKeys(): Set<string> {
    const duplicateFieldKeys = new Set<string>();   // `${blockId}::${fieldName}` of every offending editor

    for (const rule of this.uniqueTextConstraints) {
      const firstKeyByNormalizedText = new Map<string, string>();   // normalized text -> first occurrence's fieldKey

      for (const block of this.selectedBlocks.filter(b => b.name === rule.blockName)) {
        const fieldValues = block.variables ?? {};
        for (const fieldName of Object.keys(fieldValues)) {
          if (!rule.fields.some(fieldToken => fieldName === fieldToken || fieldName.startsWith(fieldToken))) continue;

          const normalizedText = Helper.normalizeForComparison(fieldValues[fieldName]);
          if (!normalizedText) continue;

          const fieldKey = `${block.blockId}::${fieldName}`;
          const firstFieldKey = firstKeyByNormalizedText.get(normalizedText);
          if (firstFieldKey) {
            duplicateFieldKeys.add(firstFieldKey);
            duplicateFieldKeys.add(fieldKey);
          } else {
            firstKeyByNormalizedText.set(normalizedText, fieldKey);
          }
        }
      }
    }

    return duplicateFieldKeys;
  }

  /**
   * Enforces `requiredFields`: returns the label of every rule with a block in
   * the template where no variable matching the rule's field token (same
   * exact-or-startsWith match as `uniqueTextConstraints`) holds a value.
   * Labels come back in configuration order.
   */
  private getMissingRequiredFields(): string[] {
    return this.requiredFields
      .filter(rule => this.selectedBlocks
        .filter(b => b.name === rule.blockName)
        .some(block => !Object.entries(block.variables ?? {}).some(([fieldName, value]) =>
          (fieldName === rule.field || fieldName.startsWith(rule.field)) && !!value?.trim()
        ))
      )
      .map(rule => rule.label);
  }

  protected onLanguageChange() {
    // grey out the screen on table change
    if (this.templateName && this.templateName != '') {
      this.greyOverlayService.enableGreyOverlay();
      this.idcustom = Helper.generateIdHash(
        `${this.templateName}-${this.selectedLanguage}`
      );
      this.updateTemplateId(this.idcustom);
      this.setTemplateData();
    }

    if (this.reorder) this.reorder = false;
    this.greyOverlayService.disableGreyOverlay();
  }

  protected isReadonly() {
    return this.viewMode == this.READONLY;
  }

  protected reloadPage() {
    location.reload();
  }

  protected clearInput() {
    this.router.navigate([this.templateBasePath]);
    this.templateName = '';
    this.idcustom = '';
    this.selectedBlocks = [];
  }

  protected isUnlocked() {
    if (this.lockedBy?.username == '' || this.lockedBy?.username == null)
      return true;
    if (this.lockedBy?.username == this.currentUser?.username) return true;
    return false;
  }

  protected setTemplateName(event: AutoCompleteSelectEvent) {
    // While typing, the autocomplete writes the raw text into the model; on
    // select it writes the whole suggestion object, so unwrap it back to a name.
    const selected: TemplateSelection | string | null = event.value;
    this.templateName =
      typeof selected === 'string' ? selected : (selected?.name ?? null);

    if (!this.templateName) {
      this.clearInput();
    } else if (this.router.url.includes(this.templateBasePath + this.templateName)) {
      return;
    } else {
      // Template is found, set the template name and get template data
      this.onSubmitTemplateName();
    }
  }

  protected setPreview() {
    this.router.navigate(['/preview'], {
      state: {
        data: {
          selectedBlocks: this.selectedBlocks,
          templateName: this.templateName,
          idcustom: this.idcustom,
          templateBasePath: this.templateBasePath,
          language: this.selectedLanguage
        },
      },
    });
  }

  protected setRedirectData() {
    const stateData: {
      selectedBlocks: BaseModel[];
      templateName: string;
      idcustom: string;
    } = history.state.data;
    if (stateData?.selectedBlocks) {
      this.selectedBlocks = stateData.selectedBlocks;
      this.templateName = stateData.templateName;
      this.idcustom = stateData.idcustom;
    }
  }

  protected setIsNew(state: boolean) {
    this.isNew = state;
    this.globalTemplateService.setIsNew(this.isNew);
    this.globalTemplateService.setIsNewTemplateData(this.isNew);
  }

  protected isMyLock() {
    return this.lockedBy.username === this.currentUser?.username;
  }

  protected copyTemplate() {
    this.isNew = true;
    this.originalTemplateName = this.templateName!;
    this.templateName = this.originalTemplateName + '_copy';
    this.isCopy = true;
    this.disableEditor(true);
    this.toogleDisableEdit(this.EDIT);
    this.removeElements(['.add-block-button', '.comments-side']);
  }

  protected translateTemplate() {
    if (!this.isNew) {
      this.handleTranslateTemplateType();
      this.isBeingTranslated = true;
      this.toogleDisableEdit(this.READONLY);
    } else {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'First create the template, after that you can translate it!',
      });
    }
  }

  private handleTranslateTemplateType() {
    this.router.navigate(['translate-list', this.idcustom]);
  }

  protected resetIds() {
    console.log('[resetIds] BEFORE regeneration:', this.selectedBlocks.map(b => ({ name: b.name, count: b.count, blockId: b.blockId })));

    //rearange ids
    this.idcustom = Helper.generateIdHash(
      `${this.templateName}-${this.selectedLanguage}`
    );

    this.updateTemplateId(this.idcustom);

    this.selectedBlocks.forEach((block) => {
      // Add timestamp to ensure uniqueness when regenerating IDs
      const newBlockId = Helper.generateIdHash(
        `${this.templateName}-${this.selectedLanguage}-${block.name}-${block.count}-${Date.now()}-${Math.random()}`
      );
      console.log(`[resetIds] Regenerating: ${block.name} count:${block.count} oldId:${block.blockId} newId:${newBlockId}`);
      block.blockId = newBlockId;
    });

    console.log('[resetIds] AFTER regeneration:', this.selectedBlocks.map(b => ({ name: b.name, count: b.count, blockId: b.blockId })));
  }

  protected confirmTranslating(confirmTranslatingModal: TemplateRef<any>) {
    this.modalService.open(confirmTranslatingModal, { size: 'lg' });
  }

  protected finishTranslating() {
    // Close the modal
    this.modalService.dismissAll();

    this.greyOverlayService.enableGreyOverlay();
    this.translateService
      .updateTemplateStatus(this.idcustom, true)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: TranslatingTemplateStatus) => {
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfull update of template status!',
          });

          this.isTranslated = res.translated;
          this.sendLockedBy();
          this.greyOverlayService.disableGreyOverlay();
        },
        error: (err) => {
          console.error('Error while updating the template status!', err);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Error while updating the template status!',
          });
          this.greyOverlayService.disableGreyOverlay();
        },
      });
  }

  /**
   * WhatsApp's "save" sends the template to Meta for approval instead of
   * writing it to Responsys (driven by the `sendsToMeta` input).
   */
  private sendTemplateToMeta(): void {
    this.activeModal.close();
    this.isApproved = true;
    this.metaStatus = MetaStatusEnum.PENDING;

    this.whatsappService.sendForApproval({
      templateName: this.templateName!,
      language: this.selectedLanguage
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isApproved = true;
          this.toastBarService.showToastSuccess("Succesfully sent the Whatsapp template to meta!")
        },
        error: (err: HttpErrorResponse) => {
          this.isApproved = false;
          console.error("Something went wrong while sending the Whatsapp template for approval!", err);
          this.toastBarService.showToastError("Something went wrong while sending the Whatsapp template for approval!");
        }
      });
  }

  protected saveToResponsys() {
    if (this.sendsToMeta) {
      this.sendTemplateToMeta();
      return;
    }

    // Reset the boolean to enable further updates
    this.isResponsysSave = false;

    this.greyOverlayService.enableGreyOverlay();
    this.isTemplateProcToResponsys = true;
    // Editing must actually lock the moment processing starts, not just show
    // the "being processed" warning - toogleDisableEdit is what really drives
    // isReadonly()/[disabled] bindings (viewMode), the warning text alone
    // doesn't disable anything.
    this.toogleDisableEdit(this.READONLY);
    this.responsysService
      .saveTemplateToResponsys({
        templateName: this.templateName!,
        idcustom: this.idcustom,
        language: this.selectedLanguage,
        templateData: this.selectedBlocks,
        reorder: this.reorder,
        templateType: this.templateType
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          // console.log('saveToResponsys res->', response);
          this.greyOverlayService.disableGreyOverlay();
          this.isTemplateProcToResponsys = true;
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Template update request accepted successfully!',
          });
        },
        error: (error) => {
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `There has been an error while saving the table to Responsys.`,
          });
          console.error(
            'There has been an error while saving the table to Responsys.',
            error
          );
          this.greyOverlayService.disableGreyOverlay();
          this.isTemplateProcToResponsys = false;
          // The submission never actually went through - re-enable editing
          // instead of leaving the template locked with nothing processing.
          this.toogleDisableEdit(this.EDIT);
        },
      });
  }

  /**
   * Reloads the current route by performing a short, hidden navigation to the app root and then
   * navigating back to the current URL. This forces Angular to reinitialize the route, its
   * resolvers/guards and recreate the component(s) without adding an extra entry for the
   * intermediate navigation to the browser history.
   *
   * Use this method when you need a clean component/route reinitialization (for example to reset
   * state or re-run route resolvers) but do not want to alter the user's navigation history.
   *
   * @protected
   * @returns void
   * @remarks
   * - Relies on this.router being available and this.router.url reflecting the intended target URL.
   * - Because it triggers navigations, avoid calling it repeatedly or inside tight loops.
   * - If route guards or resolvers perform asynchronous work, those will run again during the second
   *   navigation.
   */
  protected reloadPageClean() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  protected searchTemplate(event: AutoCompleteCompleteEvent) {
    this.searchTemplateService.checkSearchDataAtEditor(event.query, this.templateType);
  }

  protected goBack() {
    if (this.campaignId) {
      this.router.navigate(['/campaign/edit'], {
        state: {
          campaignData: {
            campaignName: "-",
            campaignId: this.campaignId,
          },
        },
      });
    } else {
      this.location.back();
    }
  }

  protected connectToCampaign(idcustom: string) {
    this.campaignDialogOpen = true;
    const dialogRef = this.dialog.open(EditTemplateToPhaseConnections, {
      // height: '600px',
      //width: '66.67vw',
      minWidth: '40vw',
      data: {
        templateId: idcustom,
        templateType: this.templateType
      }
    });
  }

  protected checkRouteParams() {
    console.log(this.route.snapshot.params);
    if (this.route.snapshot.paramMap.get('templateName')) {
      this.templateName = this.route.snapshot.paramMap.get('templateName');
    }
    if (this.route.snapshot.queryParamMap.get('campaignId')) {
      this.campaignId = this.route.snapshot.queryParamMap.get('campaignId') ?? null;
    }
    if (this.route.snapshot.queryParamMap.get('phaseId')) {
      this.phaseId = this.route.snapshot.queryParamMap.get('phaseId') ?? null;
    }
    if (this.route.snapshot.paramMap.get('language')) {
      this.selectedLanguage =
        this.route.snapshot.paramMap.get('language') || 'en';
    }
  }
}
